import {
  customVocalDials,
  filtersTouched,
  flatFilters,
  foldInstrumentName,
  intensityPhrase,
  vocalEqDial,
  type Dial,
  type InstrumentFilters,
} from "./dials";
import { eqCurvePhrase } from "./eq";
import type { SectionCue } from "./tags";

export const VOCAL_DELIVERY = [
  { id: "sung", label: "Sung" },
  { id: "rap", label: "Rap" },
  { id: "talk-sung", label: "Talk-sung" },
  { id: "choir", label: "Choir lead" },
  { id: "instrumental", label: "No vocal" },
] as const;

export const VOCAL_GENDER = [
  { id: "feminine", label: "Female" },
  { id: "masculine", label: "Male" },
  { id: "mixed", label: "Mixed" },
] as const;

export const VOCAL_REGISTER = [
  { id: "soprano", label: "Soprano" },
  { id: "alto", label: "Alto" },
  { id: "tenor", label: "Tenor" },
  { id: "baritone", label: "Baritone" },
] as const;

export const VOCAL_MIC = [
  { id: "close", label: "Close mic" },
  { id: "room", label: "Room" },
  { id: "radio", label: "Radio" },
  { id: "telephone", label: "Telephone" },
] as const;

export type VocalDelivery = (typeof VOCAL_DELIVERY)[number]["id"];
export type VocalGender = (typeof VOCAL_GENDER)[number]["id"] | "";
export type VocalRegister = (typeof VOCAL_REGISTER)[number]["id"] | "";
export type VocalMic = (typeof VOCAL_MIC)[number]["id"] | "";

export type VocalProfile = {
  delivery: VocalDelivery;
  gender: VocalGender;
  register: VocalRegister;
  mic: VocalMic;
};

export const DEFAULT_VOCAL_PROFILE: VocalProfile = {
  delivery: "sung",
  gender: "",
  register: "",
  mic: "close",
};

export type VocalLayerPreset = {
  keys: string[];
  label: string;
  timbre: string;
  level: number;
  filters: InstrumentFilters;
};

/** Standard extra voices — press on, press off. */
export const QUICK_VOCALS = [
  "Choir",
  "Gang vocals",
  "Duet",
  "Kids choir",
  "Falsetto",
  "Whisper stack",
  "Gospel",
  "Spoken word",
] as const;

export const VOCAL_LAYER_PRESETS: VocalLayerPreset[] = [
  {
    keys: ["choir", "chorus", "choral"],
    label: "Choir",
    timbre: "a many-voice choir, unison then split SATB, not a synth pad",
    level: 78,
    filters: { hpf: 28, sub: 12, bass: 28, lowmid: 48, presence: 68, air: 62, lpf: 6 },
  },
  {
    keys: ["gangvocals", "gang", "shoutchoir", "crowdvocals"],
    label: "Gang vocals",
    timbre: "loud gang shouts in unison, live-room bodies, not a polite stack",
    level: 74,
    filters: { hpf: 22, sub: 18, bass: 36, lowmid: 55, presence: 72, air: 48, lpf: 10 },
  },
  {
    keys: ["duet", "secondlead", "secondvocal", "harmonylead"],
    label: "Duet",
    timbre: "a distinct second lead sharing lines and thirds, not a quiet double",
    level: 80,
    filters: { hpf: 24, sub: 10, bass: 26, lowmid: 46, presence: 70, air: 60, lpf: 4 },
  },
  {
    keys: ["kidschoir", "childrenschoir", "kids", "children"],
    label: "Kids choir",
    timbre: "children's choir with bright head tone and unison lift",
    level: 72,
    filters: { hpf: 32, sub: 8, bass: 18, lowmid: 42, presence: 74, air: 70, lpf: 4 },
  },
  {
    keys: ["falsetto", "headvoice", "head voice"],
    label: "Falsetto",
    timbre: "a high falsetto / head-voice line, airy but pitched, not a whistle synth",
    level: 76,
    filters: { hpf: 36, sub: 6, bass: 16, lowmid: 38, presence: 66, air: 78, lpf: 2 },
  },
  {
    keys: ["whisperstack", "whisper", "whispers"],
    label: "Whisper stack",
    timbre: "layered close whispers with audible breath, rhythmic not mumbled",
    level: 70,
    filters: { hpf: 30, sub: 8, bass: 20, lowmid: 50, presence: 62, air: 72, lpf: 8 },
  },
  {
    keys: ["gospel", "gospelchoir", "gospelstack"],
    label: "Gospel",
    timbre: "gospel choir with call-and-response, big vowels and claps-ready lift",
    level: 80,
    filters: { hpf: 26, sub: 14, bass: 32, lowmid: 50, presence: 72, air: 58, lpf: 6 },
  },
  {
    keys: ["spokenword", "spoken", "spokenvocal", "narration"],
    label: "Spoken word",
    timbre: "a spoken-word / recited vocal, close and dry, not sung",
    level: 74,
    filters: { hpf: 20, sub: 16, bass: 34, lowmid: 52, presence: 70, air: 40, lpf: 12 },
  },
];

export function matchVocalLayer(name: string): VocalLayerPreset | null {
  const fold = foldInstrumentName(name);
  if (!fold) return null;
  return (
    VOCAL_LAYER_PRESETS.find((p) => p.keys.includes(fold) || foldInstrumentName(p.label) === fold) ||
    null
  );
}

export function sameVocalLayer(a: string, b: string): boolean {
  const fa = foldInstrumentName(a);
  const fb = foldInstrumentName(b);
  if (fa && fa === fb) return true;
  const pa = matchVocalLayer(a);
  const pb = matchVocalLayer(b);
  return Boolean(pa && pb && pa.label === pb.label);
}

export function vocalLayerVoice(label: string): string {
  return matchVocalLayer(label)?.timbre || `a distinct ${label.trim() || "vocal"} part, clearly audible in the mix`;
}

export function newVocalLayer(labelRaw: string, makeId: (prefix: string) => string): Dial {
  const preset = matchVocalLayer(labelRaw);
  const label = preset?.label || labelRaw.trim().slice(0, 48);
  return {
    id: makeId("vcl"),
    label,
    value: preset?.level ?? 76,
    scope: "all",
    bank: "vocals",
    custom: true,
    filters: preset ? { ...preset.filters } : flatFilters(),
  };
}

const CUE_LANG: Record<string, string> = {
  breathy: "intimate and breathy, close-miked with audible breath",
  punchy: "punchy consonants and present attack",
  whisper: "whispered, hushed delivery",
  belt: "confident belted open vowels",
  close: "close-miked and dry",
  airy: "airy, washy space on the vocal",
  spoken: "near-spoken, rhythmic talk-sing",
};

function val(dials: Dial[], id: string, fallback = 50): number {
  return dials.find((d) => d.id === id)?.value ?? fallback;
}

/** MiniMax keys off "female" / "male", not "feminine". Register implies gender if unset. */
export function resolvedVocalGender(p: Partial<VocalProfile> | undefined): "female" | "male" | "mixed" | "" {
  const g = p?.gender || "";
  if (g === "feminine") return "female";
  if (g === "masculine") return "male";
  if (g === "mixed") return "mixed";
  const r = p?.register || "";
  if (r === "soprano" || r === "alto") return "female";
  if (r === "tenor" || r === "baritone") return "male";
  return "";
}

function micPhrase(mic: VocalMic | undefined): string {
  if (mic === "room") return "room-miked with natural space";
  if (mic === "radio") return "radio-band mid presence, speaker-cone color";
  if (mic === "telephone") return "narrow telephone band, 300–3 kHz";
  if (mic === "close") return "close-miked";
  return "";
}

export function vocalLeadLock(p: Partial<VocalProfile> | undefined): string {
  const profile = p || {};
  if (profile.delivery === "instrumental") {
    return "Instrumental — no lead vocal. Do not add a singer.";
  }
  const gender = resolvedVocalGender(profile);
  const register = profile.register || "";
  const delivery =
    profile.delivery === "rap"
      ? "rapped"
      : profile.delivery === "talk-sung"
        ? "talk-sung"
        : profile.delivery === "choir"
          ? "choir-led"
          : "sung";
  const mic = micPhrase(profile.mic);
  const tail = [delivery, mic].filter(Boolean).join(", ");
  if (gender === "female") {
    return (
      `Lead vocal: female${register ? ` ${register}` : ""} singer${tail ? `, ${tail}` : ""}. ` +
      `A woman sings every lead line. Never a male lead, never a male rapper, never a default male pop singer.`
    );
  }
  if (gender === "male") {
    return (
      `Lead vocal: male${register ? ` ${register}` : ""} singer${tail ? `, ${tail}` : ""}. ` +
      `A man sings every lead line. Never a female lead, never a default female pop singer.`
    );
  }
  if (gender === "mixed") {
    return (
      `Lead vocals: mixed female and male voices${register ? ` (${register})` : ""}${tail ? `, ${tail}` : ""}. ` +
      `Both genders audible. Not a single-gender default.`
    );
  }
  return tail ? `Lead vocal: ${tail} lead with intelligible words.` : "";
}

/** For the lyrics writer — POV and range, not mix EQ. */
export function compileVocalLyricLock(p: Partial<VocalProfile> | undefined): string {
  const profile = p || {};
  if (profile.delivery === "instrumental") {
    return "Instrumental: no sung lead. Use [instrumental] only.";
  }
  const gender = resolvedVocalGender(profile);
  const register = profile.register || "";
  const delivery = profile.delivery || "sung";
  if (gender === "female") {
    return (
      `Female ${register ? `${register} ` : ""}lead (${delivery}). ` +
      `Write for a woman's voice and range. First-person narrator is a woman unless the song prompt says otherwise. ` +
      `Do not write a male-coded lead.`
    );
  }
  if (gender === "male") {
    return (
      `Male ${register ? `${register} ` : ""}lead (${delivery}). ` +
      `Write for a man's voice and range. First-person narrator is a man unless the song prompt says otherwise. ` +
      `Do not write a female-coded lead.`
    );
  }
  if (gender === "mixed") {
    return `Mixed female and male vocals (${delivery}). Two voices or an ungendered we — not a single-gender default.`;
  }
  return delivery !== "sung" ? `Delivery: ${delivery}.` : "";
}

export function compileVocalDetails(
  dials: Dial[],
  cues: SectionCue[],
  profile: VocalProfile,
): string {
  const layers = customVocalDials(dials).filter((d) => d.value > 0);
  const layerBit = layers.length
    ? ` Extra vocal layers (mandatory in the mix — press-button parts, not a generic pad): ${layers
        .map((d) => {
          const eq =
            d.filters && filtersTouched(d.filters)
              ? ` ${eqCurvePhrase(`${d.label} equalizer (this vocal only)`, d.filters)}.`
              : "";
          return `${intensityPhrase(d.label, d.value)}. Perform ${d.label} as ${vocalLayerVoice(d.label)}.${eq}`;
        })
        .join(" ")} `
    : "";

  if (profile.delivery === "instrumental") {
    if (!layers.length) {
      return (
        "Vocal Details. INSTRUMENTAL — no lead vocal, no choir, no sung doubles. " +
        "Do not add a singer. Lyrics are not performed."
      );
    }
    return (
      "Vocal Details. No solo lead singer. Lyrics are not a pop vocal. " +
      layerBit
    );
  }

  const delivery =
    profile.delivery === "rap"
      ? "rapped lead with clear cadence and consonants"
      : profile.delivery === "talk-sung"
        ? "talk-sung / sprechgesang lead"
        : profile.delivery === "choir"
          ? "choir as the lead texture, many voices in unison then split"
          : "sung lead with intelligible words on every line";

  const lock = vocalLeadLock(profile);
  const must = lock
    ? `${lock} Perform as a ${delivery}.`
    : `Lead vocal MUST be a ${delivery}.`;

  const cueLines = cues
    .filter((c) => c.cue)
    .map((c) => {
      const lang = CUE_LANG[c.cue] || c.cue;
      return `the ${c.tag.replace(/[\[\]]/g, "")} is ${lang}`;
    });

  const vocalF = vocalEqDial(dials)?.filters;
  const eqBit = vocalF
    ? ` ${eqCurvePhrase("Lead vocal equalizer (voice only)", vocalF, { alwaysBands: true })}.`
    : "";

  return (
    `Vocal Details. ${must} ` +
    `Keep the voice clearly audible above the bed; do not bury it and do not swap it for a generic pop vocal. ` +
    `Lead level: ${intensityPhrase("lead vocal", val(dials, "vocal", 70))}. ` +
    `Doubles: ${intensityPhrase("unison doubles", val(dials, "doubles", 35))}. ` +
    `Harmony/Backing Vocals: ${intensityPhrase("harmony stack", val(dials, "harmony", 40))}. ` +
    `Ad-libs: ${intensityPhrase("ad-libs", val(dials, "adlibs", 25))}. ` +
    `Breath / intimacy: ${intensityPhrase("breath", val(dials, "breath", 45))}. ` +
    `Grit / rasp: ${intensityPhrase("grit", val(dials, "grit", 20))}. ` +
    `Vibrato: ${intensityPhrase("vibrato", val(dials, "vibrato", 40))}. ` +
    `${layerBit}` +
    `Section performance: ${cueLines.join("; ") || "natural sung lead, shifting with the form"}.` +
    eqBit
  );
}
